import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActionsService } from '@app/admin/pages/risk-setup/service/actions.service';
import { AssetInfoModel, OfflineService } from '@phdsoft/common';
import { ElementInfo, Status } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { ElementDiffModel, GeometryModel, RepairModel, RepairPerTargetModel } from 'projects/phdsoft/common/src/lib/models/repairmodels/repair.model';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { firstValueFrom } from 'rxjs';
import { CriticalAreaActionsService } from 'src/app/admin/pages/risk-setup/service/critical-area-actions.service';
import { StructuralElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { environment } from 'src/environments/environment';
import * as THREE from '@phdsoft/three';
import { LightSectionModService } from 'src/app/digital-twin/Loader/LightSectionModService';
@Injectable({
  providedIn: 'root'
})
export class RepairService {

  constructor(
    private http: HttpClient,
    private offlineService: OfflineService,
    private criticalAreaActionsService: CriticalAreaActionsService,
    private actionsService: ActionsService,
    private injector: Injector,
  ) { }

  /** Lazy lookup avoids the circular DI graph (LightSectionModService injects RepairService). */
  private refreshLightSectionMods(assetId: string, regionId: string): void {
    if (!regionId) return; // can't refresh a per-region cache without knowing the region
    try {
      const svc = this.injector.get(LightSectionModService, null);
      if (svc) void svc.refresh(assetId, regionId);
    } catch {
      // Service not yet resolvable (e.g. during early bootstrap) — ignore.
    }
  }

  async upsertRepair(repair: any, assetId: string, targets: Array<any>, geometryMeshes: THREE.Mesh[] = null) {
    const vertices = [];
    const faces = [];


    for (const v of repair.SurfaceDesign.geometry.attributes.position.array) {
      vertices.push(v)
    }
    for (const v of repair.SurfaceDesign.geometry.index.array) {
      faces.push(v)
    }

    if ('criticalAreaId' in repair && repair.criticalAreaId !== undefined) {
      if(repair.isExecuted) {
        var assetActions = await firstValueFrom(this.actionsService.getActions(assetId));
        var undividedRepairCutAction = assetActions.find(x => x.TypeId == "898c1836-a638-465b-b2fc-89c55d1925dd");
        if(undividedRepairCutAction) {
          this.criticalAreaActionsService.completeCriticalAreaAction(repair.criticalAreaId, undividedRepairCutAction?.Id)
        }
      }
      console.log("Critical Area ID found:", repair.criticalAreaId);
    }


    let differences: Array<ElementDiffModel> = [];
    for (const target of targets) {
      const vert = [];
      const fac = [];
      for (const v of target.SurfaceDesign.geometry.attributes.position.array) {
        vert.push(v)
      }
      for (const v of target.SurfaceDesign.geometry.index.array) {
        fac.push(v)
      }

      let g: GeometryModel = {
        GeometryF: fac,
        GeometryV: vert,
        Date: repair.date
      };

      // let oldG: GeometryModel = target.Geometries[target.Geometries.length - 2].geometryModel;
      // if (target.Geometries && target.Geometries.length > 0)
      {
        let t: ElementDiffModel = {
          ElementId: target.Guid,
          TypeId: target.TypeId,
          Id: repair.Guid,
          Date: repair.date,
          NewGeometry3D: null,
          OldGeometry3D: g
        };
        differences.push(t);
      }
    }
    let elementInfoTargets: Array<RepairPerTargetModel> = [];
    if(geometryMeshes) {  
      for (const element of geometryMeshes) {
        let vert = [];
        let fac = [];
        for (const v of (element.geometry as any).attributes.position.array) {
          vert.push(v)
        }
        for (const v of (element.geometry as any).index.array) {
          fac.push(v)
        }
    

        let elementInfoTarget: RepairPerTargetModel = {
          TargetId: element.name,
          ElementInfo: {
            type: '',
            vertices: vert,
            faces: fac
          }
        };
        elementInfoTargets.push(elementInfoTarget);

      }
    }

    let elementInfo: ElementInfo = {
      type: '',
      vertices: vertices,
      faces: faces
    };
    let _data: RepairModel = {
      Label: repair.properties.General.Label,
      AssetId: assetId,
      RegionId: repair.regionId,
      RegionIds: repair.regionIds,
      SectionId: repair.SectionId,
      ElementId: repair.Guid,
      TypeId: targets[0].TypeId,
      Date: repair.date,
      Status: repair.isExecuted,
      Doubler: repair.isDoubler,
      RepairTypeId: repair.isDoubler ? ELEMENTSTYPESIDS.REPAIRDOUBLER : ELEMENTSTYPESIDS.REPAIR,
      Id: repair.Guid,
      TargetId: repair.targetId,
      TargetIds: repair.targetIds,
      ElementInfo: elementInfo,
      ElementInfoTargets: elementInfoTargets,
      InspectionsIds: repair.inspectionsSolved.map(x => x.Guid),
      properties: repair.properties,
      ElementsViewModelDiff: differences
    };
    const result = await this.http.post<void>(
      environment.API_THREED + `Repair/AddRepair`,
      _data,
    ).toPromise();
    this.refreshLightSectionMods(assetId, repair.regionId);
    return result;
  }

  public async deleteRepair(repair: StructuralElement, assetId: string) {
    const data = { Id: repair.Guid, RegionId: repair.regionId, RegionIds: repair.regionIds, SectionId: repair.SectionId, Date: repair.date, AssetId: assetId };
    const result = await this.http.post<void>(environment.API_THREED + `Repair/DeleteRepair`, data).toPromise();
    this.refreshLightSectionMods(assetId, repair.regionId);
    return result;
  }

  public async getRepairElements(assetId: string, regionId: string, referenceDate: any): Promise<any> {
    let _RESPONSE = null;
    if (window.navigator.onLine) {
      _RESPONSE = await this.http.get<AssetInfoModel>(
        environment.API_THREED + `Repair/GetRepairs?assetId=${assetId}&regionId=${regionId}&referenceDate=${referenceDate}`
      ).toPromise();

    } else {
      let dbStoreData = await this.offlineService.getItem(`repairs_${regionId}`)
      _RESPONSE = dbStoreData?.json
    }

    return _RESPONSE
  }

  public async getRepairElementsCount(assetId: string, regionId: string, startDate: any, endDate:any, doubler?: boolean): Promise<any> {
    let _RESPONSE = null;
    if (window.navigator.onLine) {
      const doublerParam = doubler !== undefined ? `&doubler=${doubler}` : '';
      _RESPONSE = await this.http.get<any>(
        environment.API_THREED + `Repair/CountRepairs?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}${doublerParam}`
      ).toPromise();

    }
    return _RESPONSE
  }

  getRepairElementsCountSubscriber(assetId: string, regionId: string, startDate: any, endDate:any, doubler?: boolean) {
    const preventCache = (new Date()).getTime();
    const doublerParam = doubler !== undefined ? `&doubler=${doubler}` : '';
    return this.http.get<any>(environment.API_THREED + `Repair/CountRepairs?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}${doublerParam}`)
  }


  public async solveInspection(repair: any, assetId: string, targets: Array<any>) {
    const vertices = [];
    const faces = [];

    for (const v of repair.SurfaceDesign.geometry.attributes.position.array) {
      vertices.push(v)
    }
    for (const v of repair.SurfaceDesign.geometry.index.array) {
      faces.push(v)
    }


    let differences: Array<ElementDiffModel> = [];
    for (const target of targets) {
      const vert = [];
      const fac = [];
      for (const v of target.SurfaceDesign.geometry.attributes.position.array) {
        vert.push(v)
      }
      for (const v of target.SurfaceDesign.geometry.index.array) {
        fac.push(v)
      }

      let g: GeometryModel = {
        GeometryF: fac,
        GeometryV: vert,
        Date: repair.date
      };

      // let oldG: GeometryModel = target.Geometries[target.Geometries.length - 2].geometryModel;
      // if (target.Geometries && target.Geometries.length > 0)
      {
        let t: ElementDiffModel = {
          ElementId: target.Guid,
          TypeId: target.TypeId,
          Id: repair.Guid,
          Date: repair.date,
          NewGeometry3D: null,
          OldGeometry3D: g
        };
        differences.push(t);
      }
    }

    let elementInfo: ElementInfo = {
      type: '',
      vertices: vertices,
      faces: faces
    };
    let _data: RepairModel = {
      Label: repair.properties.General.Label,
      AssetId: assetId,
      RegionId: repair.regionId,
      RegionIds: repair.regionIds,
      SectionId: repair.SectionId,
      ElementId: repair.Guid,
      TypeId: targets[0].TypeId,
      Date: repair.date,
      Status: repair.isExecuted,
      Doubler: repair.isDoubler,
      RepairTypeId: repair.isDoubler ? ELEMENTSTYPESIDS.REPAIRDOUBLER : ELEMENTSTYPESIDS.REPAIR,
      Id: repair.Guid,
      TargetId: repair.targetId,
      TargetIds: repair.targetIds,
      ElementInfo: elementInfo,
      InspectionsIds: repair.inspectionsSolved.map(x => x.Guid),
      properties: repair.properties,
      ElementsViewModelDiff: differences
    };
    return await this.http.post<Array<string>>(
      environment.API_INSPEC + `Inspection/SolveInspections`,
      _data,
    ).toPromise();
  }

  // reActivateRepairInspections(assetId: string, regionId: string, startDate: any, endDate:any) {
  //   const preventCache = (new Date()).getTime();
  //   return this.http.get<any>(environment.API_INSPEC + `Repair/CountRepairs?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}`)
  // }

  public async reActivateRepairInspections(repair: any, assetId: string, targets: Array<any>) {
    const vertices = [];
    const faces = [];

    for (const v of repair.SurfaceDesign.geometry.attributes.position.array) {
      vertices.push(v)
    }
    for (const v of repair.SurfaceDesign.geometry.index.array) {
      faces.push(v)
    }


    let differences: Array<ElementDiffModel> = [];
    for (const target of targets) {
      const vert = [];
      const fac = [];
      for (const v of target.SurfaceDesign.geometry.attributes.position.array) {
        vert.push(v)
      }
      for (const v of target.SurfaceDesign.geometry.index.array) {
        fac.push(v)
      }

      let g: GeometryModel = {
        GeometryF: fac,
        GeometryV: vert,
        Date: repair.date
      };

      // let oldG: GeometryModel = target.Geometries[target.Geometries.length - 2].geometryModel;
      // if (target.Geometries && target.Geometries.length > 0)
      {
        let t: ElementDiffModel = {
          ElementId: target.Guid,
          TypeId: target.TypeId,
          Id: repair.Guid,
          Date: repair.date,
          NewGeometry3D: null,
          OldGeometry3D: g
        };
        differences.push(t);
      }
    }

    let elementInfo: ElementInfo = {
      type: '',
      vertices: vertices,
      faces: faces
    };
    let _data: RepairModel = {
      Label: repair.properties.General.Label,
      AssetId: assetId,
      RegionId: repair.regionId,
      RegionIds: repair.regionIds,
      SectionId: repair.SectionId,
      ElementId: repair.Guid,
      TypeId: targets[0].TypeId,
      Date: repair.date,
      Status: repair.isExecuted,
      Doubler: repair.isDoubler,
      RepairTypeId: repair.isDoubler ? ELEMENTSTYPESIDS.REPAIRDOUBLER : ELEMENTSTYPESIDS.REPAIR,
      Id: repair.Guid,
      TargetId: repair.targetId,
      TargetIds: repair.targetIds,
      ElementInfo: elementInfo,
      InspectionsIds: repair.inspectionsSolved.map(x => x.Guid),
      properties: repair.properties,
      ElementsViewModelDiff: differences
    };
    return await this.http.post<Array<string>>(
      environment.API_INSPEC + `Inspection/ReactivateInspections`,
      _data,
    ).toPromise();
  }


  public async GenerateWeightMeasureBox(minX:number, maxX:number,minY:number, maxY:number, minZ:number, maxZ:number, assetId: string, regionsIds:Array<string>) {

    let weightMeasureBoxRequestViewModel = {
      AssetId:assetId,
      RegionIds:regionsIds,
      xMax:maxX,
      yMax:maxY,
      zMax:maxZ,
      xMin:minX,
      yMin:minY,
      zMin:minZ,
    }

    return await this.http.post<any>(environment.API_THREED + `Elements/GenerateWeightMeasureBox`, weightMeasureBoxRequestViewModel).toPromise();
  }

  async upsertCutPlate(
    cutShape: any,
    assetId: string,
    targets: Array<any>,
    geometryMeshes: THREE.Mesh[] = null
  ) {
    const vertices = [];
    const faces = [];

    const cutGeometry = cutShape.SurfaceDesign.geometry;
    for (const v of cutGeometry.attributes.position.array) {
      vertices.push(v);
    }
    // Handle both indexed and non-indexed geometries
    if (cutGeometry.index) {
      for (const v of cutGeometry.index.array) {
        faces.push(v);
      }
    } else {
      // Non-indexed geometry: generate sequential indices
      const vertexCount = cutGeometry.attributes.position.count;
      for (let i = 0; i < vertexCount; i++) {
        faces.push(i);
      }
    }

    let elementInfoTargets: Array<RepairPerTargetModel> = [];
    if (geometryMeshes) {
      for (const element of geometryMeshes) {
        let vert = [];
        let fac = [];
        const elemGeometry = element.geometry as any;
        for (const v of elemGeometry.attributes.position.array) {
          vert.push(v);
        }
        // Handle both indexed and non-indexed geometries
        if (elemGeometry.index) {
          for (const v of elemGeometry.index.array) {
            fac.push(v);
          }
        } else {
          // Non-indexed geometry: generate sequential indices
          const vertexCount = elemGeometry.attributes.position.count;
          for (let i = 0; i < vertexCount; i++) {
            fac.push(i);
          }
        }

        let elementInfoTarget: RepairPerTargetModel = {
          TargetId: element.name,
          ElementInfo: {
            type: '',
            vertices: vert,
            faces: fac,
          },
        };
        elementInfoTargets.push(elementInfoTarget);
      }
    }

    let elementInfo: ElementInfo = {
      type: '',
      vertices: vertices,
      faces: faces,
    };

    let _data: RepairModel = {
      Label: 'CutPlate',
      AssetId: assetId,
      RegionId: cutShape.regionId,
      RegionIds: cutShape.regionIds || [cutShape.regionId],
      SectionId: cutShape.SectionId,
      ElementId: cutShape.Guid,
      TypeId: targets[0]?.TypeId || '',
      Date: cutShape.date || new Date(),
      Status: Status.Executed,
      Doubler: false,
      RepairTypeId: ELEMENTSTYPESIDS.REPAIR,
      CutOnly: true, // This is the key difference
      Id: cutShape.Guid,
      TargetId: cutShape.targetId,
      TargetIds: cutShape.targetIds,
      ElementInfo: elementInfo,
      ElementInfoTargets: elementInfoTargets,
      InspectionsIds: [],
      properties: null,
      ElementsViewModelDiff: [],
    };

    const result = await this.http
      .post<any>(environment.API_THREED + `Repair/AddRepair`, _data)
      .toPromise();
    this.refreshLightSectionMods(assetId, cutShape.regionId);
    return result;
  }

  /**
   * Deletes a cut-plate-only diff and recomputes remaining diffs on the same element.
   * @param assetId The asset ID
   * @param regionId The region ID
   * @param sectionId The section ID
   * @param diffId The ID of the ElementViewModelDiff to delete
   * @param referenceDate The reference date to search for the repair manager file
   * @returns Updated RepairManagerViewModel after deletion and recomputation
   */
  async deleteCutPlate(
    assetId: string,
    regionId: string,
    sectionId: string,
    diffId: string,
    referenceDate: Date
  ): Promise<any> {
    const params = new URLSearchParams({
      assetId,
      regionId,
      sectionId,
      diffId,
      referenceDate: referenceDate.toISOString()
    });

    const result = await this.http
      .delete<any>(environment.API_THREED + `Repair/DeleteCutPlate?${params.toString()}`)
      .toPromise();
    this.refreshLightSectionMods(assetId, regionId);
    return result;
  }

  public async getRegionLightSectionMods(assetId: string, regionId: string): Promise<any[]> {
    if (window.navigator.onLine) {
      const data = await this.http.get<any[]>(
        environment.API_THREED + `LightSectionMod/GetRegionMods?assetId=${assetId}&regionId=${regionId}`
      ).toPromise();
      return data ?? [];
    }
    const dbStoreData = await this.offlineService.getItem(`lightsectionmods_${assetId}_${regionId}`);
    return (dbStoreData?.json as any[]) ?? [];
  }

  public async getLightSectionModsForSection(assetId: string, regionId: string, sectionId: string, referenceDate: Date | string): Promise<any[]> {
    const refIso = typeof referenceDate === 'string' ? referenceDate : referenceDate.toISOString();
    const data = await this.http.get<any[]>(
      environment.API_THREED + `LightSectionMod/GetMods?assetId=${assetId}&regionId=${regionId}&sectionId=${sectionId}&referenceDate=${refIso}`
    ).toPromise();
    return data ?? [];
  }

  public async getLightSectionModManifest(assetId: string): Promise<any> {
    return await this.http.get<any>(
      environment.API_THREED + `LightSectionMod/GetManifest?assetId=${assetId}`
    ).toPromise();
  }
}
